import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  Output,
  EventEmitter,
  Input,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { LocationService } from '@fitness-force/api';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { StyleClassModule } from 'primeng/styleclass';
import { Store } from '@ngrx/store';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { LOCATION_STATE } from 'apps/ff-frontend/src/app/state/app.state';

@Component({
  selector: 'ff-google-places',
  templateUrl: './google-places.component.html',
  styleUrls: ['./google-places.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    StyleClassModule
  ]
})
export class GooglePlacesComponent implements OnInit, OnChanges {
  @ViewChild('search') searchElementRef: ElementRef | undefined;
  @ViewChild('map') mapElementRef: ElementRef | undefined;
  @Output() fetchPlaceInfo: any = new EventEmitter();
  @Input() competitorName: AbstractControl | any;
  @Input() placeId: string | any;
  @Input() placeHolderName: string | any;
  @Input() showMap: boolean = false;
  @Input() placeDetails: any = null;
  isPlaceSearched = false;

  isLocationSelected = false;
  filteredPlaces: any[] | undefined;

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private locationService: LocationService,
    private store: Store
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit() {
    //await timeout(2000);
    this.loadGoogleMapsAPI()
      .then(() => {
        if (!this.isGoogleMapsAvailable()) {
          console.error('Google Maps API not available after loading');
          return;
        }
        
        if (this.placeId) {
          // Use Places API (with automatic fallback to deprecated PlacesService if needed)
          this.getPlaceDetailsById(this.placeId)
            .then((placeResult) => {
              // Handle both new and legacy API response formats
              const formattedAddress = placeResult.formattedAddress || placeResult.formatted_address;
              this.competitorName.setValue(formattedAddress);
              this.placeDetails = placeResult;
              this.initGoogleMaps();
            })
            .catch((error) => {
              console.error('Error fetching place details:', error);
            });
        }
        if (!this.showMap) this.initAutocomplete();
        else this.initGoogleMaps();
      })
      .catch((error) => {
        console.error('Failed to load Google Maps API:', error);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle changes to the selectedOption input
    if (
      changes.placeDetails &&
      changes.placeDetails.currentValue != changes.placeDetails.previousValue
    ) {
      this.initGoogleMaps();
    }
  }

  loadGoogleMapsAPI(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if Google Maps is already loaded
      if (this.isScriptLoaded() && this.isGoogleMapsAvailable()) {
        resolve();
        return;
      }
      
      // If script is loaded but Google Maps not available, wait for it
      if (this.isScriptLoaded()) {
        this.waitForGoogleMaps(resolve, reject);
        return;
      }
      
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.GOOGLE_PLACES}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.id = 'google-maps-api';
      document.head.appendChild(script);
      
      script.onload = () => {
        this.waitForGoogleMaps(resolve, reject);
      };
      
      script.onerror = (error) => {
        reject(error);
      };
    });
  }

  private waitForGoogleMaps(resolve: () => void, reject: (error: any) => void, attempts: number = 0): void {
    const maxAttempts = 50; // 5 seconds max wait time
    const interval = 100; // Check every 100ms

    if (this.isGoogleMapsAvailable()) {
      resolve();
      return;
    }

    if (attempts >= maxAttempts) {
      reject(new Error('Google Maps Places library not available after waiting'));
      return;
    }

    setTimeout(() => {
      this.waitForGoogleMaps(resolve, reject, attempts + 1);
    }, interval);
  }

  private isGoogleMapsAvailable(): boolean {
    return typeof google !== 'undefined' && 
           !!google.maps && 
           !!google.maps.places && 
           !!google.maps.places.Autocomplete && 
           !!google.maps.places.PlacesService;
  }

  /**
   * Get place details by place ID
   * TODO: Migrate to google.maps.places.Place API when available
   * Current implementation uses deprecated PlacesService
   */
  private getPlaceDetailsById(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      // Check if new Place API is available (future-proofing)
      if ((google.maps.places as any).Place) {
        this.getPlaceDetailsWithNewAPI(placeId)
          .then(resolve)
          .catch(() => {
            // Fallback to legacy API if new API fails
            this.getPlaceDetailsWithLegacyAPI(placeId, resolve, reject);
          });
      } else {
        // Use legacy PlacesService (deprecated but still functional)
        console.warn('Using deprecated PlacesService. Consider migrating to google.maps.places.Place when available.');
        this.getPlaceDetailsWithLegacyAPI(placeId, resolve, reject);
      }
    });
  }

  private async getPlaceDetailsWithNewAPI(placeId: string): Promise<any> {
    // This will be used when the new Place API becomes available
    const PlaceConstructor = (google.maps.places as any).Place;
    const place = new PlaceConstructor({
      id: placeId,
    });

    await place.fetchFields({
      fields: [
        'id',
        'displayName', 
        'formattedAddress',
        'geometry',
        'location',
        'name',
        'place_id'
      ]
    });

    return {
      place_id: place.id,
      formatted_address: place.formattedAddress,
      name: place.displayName,
      geometry: {
        location: place.location
      }
    };
  }

  private getPlaceDetailsWithLegacyAPI(placeId: string, resolve: (value: any) => void, reject: (reason?: any) => void): void {
    // Use appropriate element for PlacesService - create a temporary div if needed
    let serviceElement = this.showMap ? this.mapElementRef?.nativeElement : this.searchElementRef?.nativeElement;
    
    if (!serviceElement) {
      // Create temporary element for PlacesService if no suitable element exists
      serviceElement = document.createElement('div');
    }

    const placesService = new google.maps.places.PlacesService(serviceElement);
    
    placesService.getDetails(
      {
        placeId: placeId,
        fields: [
          'place_id',
          'formatted_address', 
          'name',
          'geometry'
        ]
      },
      (placeResult, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && placeResult) {
          resolve(placeResult);
        } else {
          reject(new Error(`Places service failed with status: ${status}`));
        }
      }
    );
  }

  initAutocomplete(): void {
    // Ensure Google Maps JavaScript API is loaded before calling this method
    if (!this.searchElementRef?.nativeElement) {
      console.error('Search element not available for autocomplete');
      return;
    }

    this.store.select(LOCATION_STATE).subscribe((state) => {
      if (state && state.loc != '') {
        const autocomplete = new google.maps.places.Autocomplete(
          this.searchElementRef?.nativeElement,
          {
            types: ['address'],
            bounds: new google.maps.LatLngBounds(
              new google.maps.LatLng(Number(state.loc.split(',')[0]), Number(state.loc.split(',')[1])),
            )
            //componentRestrictions: { country: 'us' }, // Adjust as needed
          }
        );
        autocomplete.addListener('place_changed', () => {
          this.ngZone.run(() => {
            // Get the place details from the autocomplete object.
            const place = autocomplete.getPlace();
            if (place.geometry === undefined || place.geometry === null) {
              return;
            }
            this.fetchPlaceInfo.emit(place);
            this.changeDetectorRef.detectChanges();
          });
        });
      }
      else{
        this.locationService.getIpData().subscribe((ipData) => {
          if (!this.searchElementRef?.nativeElement) return;
          const autocomplete = new google.maps.places.Autocomplete(
            this.searchElementRef?.nativeElement,
            {
              types: ['address'],
              bounds: new google.maps.LatLngBounds(
                new google.maps.LatLng(Number(ipData.loc.split(',')[0]), Number(ipData.loc.split(',')[1])),
              )
              //componentRestrictions: { country: 'us' }, // Adjust as needed
            }
          );
          autocomplete.addListener('place_changed', () => {
            this.ngZone.run(() => {
              // Get the place details from the autocomplete object.
              const place = autocomplete.getPlace();
              if (place.geometry === undefined || place.geometry === null) {
                return;
              }
              this.fetchPlaceInfo.emit(place);
              this.changeDetectorRef.detectChanges();
            });
          });
        });
      }
    });
    // const autocomplete = new google.maps.places.Autocomplete(
    //   this.searchElementRef?.nativeElement,
    //   {
    //     types: ['address'],
    //     bounds: new google.maps.LatLngBounds(
    //       new google.maps.LatLng(28.20453, 76.82718),
    //       new google.maps.LatLng(28.20453, 76.82718)
    //     )
    //     //componentRestrictions: { country: 'us' }, // Adjust as needed
    //   }
    // );

    // autocomplete.addListener('place_changed', () => {
    //   this.ngZone.run(() => {
    //     // Get the place details from the autocomplete object.
    //     const place = autocomplete.getPlace();
    //     if (place.geometry === undefined || place.geometry === null) {
    //       return;
    //     }
    //     this.fetchPlaceInfo.emit(place);
    //     this.changeDetectorRef.detectChanges();
    //   });
    // });
  }

  initGoogleMaps() {
    if (!this.mapElementRef?.nativeElement) {
      console.error('Map element not available for Google Maps initialization');
      return;
    }

    let googleMaps: any;
    if (this.placeDetails != null) {
      googleMaps = new google.maps.Map(this.mapElementRef.nativeElement, {
        center: {
          lat: Number(this.placeDetails.geometry.location.lat()),
          lng: Number(this.placeDetails.geometry.location.lng()),
        },
        zoom: 15,
      });
      new google.maps.Marker({
        map: googleMaps,
        position: this.placeDetails.geometry.location,
        title: this.placeDetails.name,
      });
      googleMaps.setCenter(this.placeDetails.geometry.location);
      googleMaps.setZoom(15);
    } else {
      this.locationService.getIpData().subscribe((ipData) => {
        googleMaps = new google.maps.Map(this.mapElementRef!.nativeElement, {
          center: {
            lat: Number(ipData.loc.split(',')[0]),
            lng: Number(ipData.loc.split(',')[1]),
          },
          zoom: 15,
        });
      });
    }
  }

  closePlaceDailog() {
    this.isLocationSelected = false;
  }

  onAutocompleteSelected(result: any) {
    console.log('onAutocompleteSelected: ', result);
  }

  onLocationSelected(location: Location) {
    console.log('onLocationSelected: ', location);
  }

  isScriptLoaded(): boolean {
    return Array.from(document.getElementsByTagName('script')).some(
      (script) => script.id === 'google-maps-api'
    );
  }
}